import React from 'react';
import { FiMoreVertical, FiEdit2, FiTrash2, FiCheck } from 'react-icons/fi';
import { Button, Card, Menu, MenuTrigger, MenuContent, MenuItem, MenuSeparator } from '../../design-system';
import { Sample, CalculationStrategies } from '../../@types/samples/types';
import { analysesWithResult } from '../MultiAnalysisPage/analysisCategory';
import styles from './CapillaryPressurePage.module.css';

const fmt = (v: number | null | undefined) =>
  v != null ? v.toFixed(4) : '—';

const ANALYSIS_LABELS: Record<CalculationStrategies, string> = {
  [CalculationStrategies.LIAO]: 'Liao',
  [CalculationStrategies.LAMBDA]: 'Brooks Corey',
  [CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY]: 'Water-Oil',
  [CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY]: 'Gas-Oil',
};

const ANALYSIS_CLASSES: Record<CalculationStrategies, string> = {
  [CalculationStrategies.LIAO]: styles.tagLiao,
  [CalculationStrategies.LAMBDA]: styles.tagLambda,
  [CalculationStrategies.WATER_OIL_RELATIVE_PERMEABIILITY]: styles.tagWaterOil,
  [CalculationStrategies.GAS_OIL_RELATIVE_PERMEABIILITY]: styles.tagGasOil,
};

type IconProps = { size?: number; color?: string };
const MoreIcon = FiMoreVertical as React.FC<IconProps>;
const EditIcon = FiEdit2 as React.FC<IconProps>;
const TrashIcon = FiTrash2 as React.FC<IconProps>;
const CheckIcon = FiCheck as React.FC<IconProps>;

type Props = {
  sample: Sample;
  onClick: () => void;
  onEdit: (sample: Sample) => void;
  onRemove: (sample: Sample) => void;
  removing?: boolean;
  selectable?: boolean;
  selected?: boolean;
};

const SampleCard = ({ sample, onClick, onEdit, onRemove, removing, selectable, selected }: Props) => {
  const v = sample.input_sample_values;
  const depthLabel = sample.depth != null
    ? `${sample.depth}${sample.depth_unit ? ` ${sample.depth_unit}` : ''}`
    : '—';

  const props: { label: string; value: string }[] = [
    { label: 'Porosity', value: fmt(v.porosity) },
    { label: 'Permeability', value: fmt(v.permeability) },
    { label: 'FZI', value: fmt(v.fzi) },
    { label: 'Swi', value: fmt(v.swi) },
  ];

  const menuAction = (
    <Menu>
      <MenuTrigger>
        <Button
          variant="ghost"
          size="sm"
          className={styles.cardMenuTrigger}
          onClick={e => e.stopPropagation()}
          aria-label="Sample options"
        >
          <MoreIcon size={15} />
        </Button>
      </MenuTrigger>
      <MenuContent align="end">
        <MenuItem
          icon={<EditIcon size={14} />}
          onClick={e => {
            e.stopPropagation();
            onEdit(sample);
          }}
        >
          Edit
        </MenuItem>
        <MenuSeparator />
        <MenuItem
          icon={<TrashIcon size={14} />}
          variant="destructive"
          onClick={e => {
            e.stopPropagation();
            onRemove(sample);
          }}
        >
          Remove
        </MenuItem>
      </MenuContent>
    </Menu>
  );

  return (
    <Card
      variant="default"
      padding="none"
      interactive
      fullWidth
      onClick={removing ? undefined : onClick}
      className={selected ? styles.sampleCardSelected : undefined}
      style={removing ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
    >
      <Card.Header
        title={
          <span className={styles.sampleCardTitle}>
            {selectable && (
              <span
                className={`${styles.sampleCheckbox} ${selected ? styles.sampleCheckboxOn : ''}`}
                aria-hidden="true"
              >
                {selected ? <CheckIcon size={11} /> : null}
              </span>
            )}
            {sample.sample_code || '—'}
          </span>
        }
        subtitle={`Depth: ${depthLabel}`}
        action={selectable ? null : menuAction}
      />
      <Card.Body>
        <dl className={styles.sampleProps}>
          {props.map(({ label, value }) => (
            <div key={label} className={styles.samplePropItem}>
              <dt className={styles.samplePropLabel}>{label}</dt>
              <dd className={styles.samplePropValue}>{value}</dd>
            </div>
          ))}
        </dl>

        {analysesWithResult(sample).length > 0 && (
          <div className={styles.analysisTags}>
            {analysesWithResult(sample).map(strategy => (
              <span key={strategy} className={`${styles.analysisTag} ${ANALYSIS_CLASSES[strategy]}`}>
                {ANALYSIS_LABELS[strategy]}
              </span>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default SampleCard;
